// Shared low-poly industry parts — a gable roof (the repeated eave/slope math)
// and the layered glowing ember mouth (the signature accent of every
// fire-driven trade: a bakery oven, a smelter tap, a goldsmith crucible, a
// refinery fire-box). Ported from age-of-mcp's v2/industry-parts.ts: the pure
// geometry is kept verbatim, with the Part type taken from the library.

import * as THREE from "three";
import type { Part } from "../types";

/**
 * A gable roof with its ridge along x: two sloped slabs meeting at
 * `eaveY + rise`, spanning `width` (x) by `depth` (z), centred at (cx, cz).
 * Pass `ridgeColor` to cap the ridge beam.
 */
export function gableRoof(
  cx: number, cz: number, eaveY: number,
  width: number, depth: number, rise: number,
  color: string, ridgeColor?: string,
): Part[] {
  const halfD = depth / 2;
  const slope = Math.hypot(halfD, rise);
  const ang = Math.atan2(rise, halfD);
  const parts: Part[] = [
    { geometry: new THREE.BoxGeometry(width, 0.06, slope), color,
      position: [cx, eaveY + rise / 2, cz + halfD / 2], rotation: [ang, 0, 0] },
    { geometry: new THREE.BoxGeometry(width, 0.06, slope), color,
      position: [cx, eaveY + rise / 2, cz - halfD / 2], rotation: [-ang, 0, 0] },
  ];
  if (ridgeColor) {
    parts.push({ geometry: new THREE.BoxGeometry(width + 0.04, 0.07, 0.07),
      color: ridgeColor, position: [cx, eaveY + rise, cz] });
  }
  return parts;
}

/**
 * A glowing fire mouth facing +z: a dark arch frame, an orange glow and a
 * bright ember core layered slightly forward of each other. Pass `name` to tag
 * the two glow layers (not the frame) so an animation can pulse them — e.g. a
 * `firePulse(name, [x, y, z])` track in a building's "active" animation.
 */
export function emberMouth(x: number, y: number, z: number, w = 0.3, h = 0.22, name?: string): Part[] {
  return [
    { geometry: new THREE.BoxGeometry(w, h, 0.05), color: "#241c15", position: [x, y, z] },
    { geometry: new THREE.BoxGeometry(w * 0.72, h * 0.66, 0.05), color: "#f08a30",
      position: [x, y - h * 0.05, z + 0.015], name },
    { geometry: new THREE.BoxGeometry(w * 0.4, h * 0.36, 0.05), color: "#ffd24a",
      position: [x, y - h * 0.12, z + 0.03], name },
  ];
}
